/**
 * OrbFan — the entire mobile navigation of every HOTTR surface.
 *
 * Screen System §1: there is no bottom tab bar, no centre nav and no hamburger
 * anywhere in the ecosystem. The Orb is the single persistent control.
 *
 *   Tap the Orb   → the existing submenu opens in place (the route fan)
 *   Hold the Orb  → Hottie sheet (Chat + Voice)
 *
 * The Orb and the fan ship as ONE component rather than two, because they are
 * one control and the gestures belong in one place. Splitting them is how you
 * end up with a surface whose long-press does nothing.
 *
 * Worlds-switch still lives on the header suffix. The tail's WORLDS item is
 * the second door into that same panel — it does not navigate.
 *
 * Structure, top to bottom (the Orb is at the bottom, so the list counts up
 * away from the thumb):
 *
 *     CONTEXT     1–3 actions, varies by route
 *     ─────────   hard hairline
 *     ROUTES      fixed order
 *     MEDIA       only while something is playing, nearest the Orb
 *     [ORB]
 *
 * Media sits below the routes because it is the thing you opened the fan FOR
 * while audio is running, and the row nearest the thumb is the one you reach
 * without looking. It is not a route, it does not persist, and when nothing is
 * playing the fan is exactly what it was.
 *
 * Geometry comes from fanGeometry.ts, which is the drawing's own arithmetic.
 * This file positions; chassis.css styles; neither invents a number.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import './chassis.css';
import './fan-media.css';
import { cn } from './cn';
import { fanOffsets, fanPath } from './fanGeometry';
import { HottrOrb, type OrbState } from './HottrOrb';
import { Icon } from './Icon';
import { ROUTE_ICONS } from './routeIcons';
import { HottieSheet } from './HottieSheet';
import { useHottieVoice } from './hottieVoice';
import { useOrbHold } from './orbHold';
import type { HottieMessage, HottieMode } from './hottie';
import {
  mediaIndicator,
  mediaIsLive,
  mediaToggleIcon,
  mediaToggleLabel,
  type OrbIndicator,
  type OrbMedia,
} from './media';
import { closeWorldsMenu, openWorldsMenu } from './worldsControl';
import { HAND_DEFAULT, routeLabel, worldRoutes, type Hand, type HottrProduct } from './worlds';

export interface OrbFanProps {
  /** Which world's route vocabulary to render. */
  product: HottrProduct;
  /** The route currently shown, so the fan can mark it. */
  activeRoute?: string;
  /**
   * The 1–3 context actions for the current route — `FILTER · SWITCH TO GRID`
   * on People, `SAVE · SHARE · BLOCK` on a Calling Card. Context is the only
   * part of the fan that varies.
   */
  context?: string[];
  /** Which side the Orb lives on. One account setting flips both tiers. */
  hand?: Hand;
  /**
   * Include HOTTIE in the fixed tail. `false` only for a surface with no
   * assistant behind it — the brand reference site. The worlds door always stays.
   */
  hottie?: boolean;
  /**
   * World routes this app has not built yet, hidden rather than rendered as
   * controls that go nowhere. A short menu beats a lying one.
   */
  omitRoutes?: readonly string[];
  /**
   * Replace the world's route list. The brand reference site uses this so the
   * fan names real pages instead of unused stubs.
   */
  fanRoutes?: readonly string[];
  /**
   * The Orb's resting behaviour. `activated` is not accepted here: the fan
   * owns that state, and it means "the fan is open".
   */
  orbState?: Extract<OrbState, 'resting' | 'interested'>;
  /** Orb diameter. 56px on mobile per §2 — change only with a reason. */
  orbSize?: number;
  /** Override or extend the shared route → glyph mapping. */
  routeIcons?: Record<string, string>;
  onNavigate?: (route: string) => void;
  onContext?: (action: string) => void;
  /**
   * Override the in-kit Hottie sheet. Hosts that already open Hottie in place
   * pass this; everyone else gets the sheet with no extra wiring.
   */
  onHottie?: () => void;
  /** Transcript for the in-kit sheet. Kit does not invent replies. */
  hottieMessages?: readonly HottieMessage[];
  /** Called when the composer sends. Kit does not talk to a model. */
  onHottieSend?: (text: string) => void;
  /** Voice Stack slot, rendered in Voice mode. No second Orb. */
  hottieVoice?: ReactNode;
  /**
   * Something the host is playing. Renders a transport nearest the Orb and
   * puts the Orb itself into its alive state — warmer material and a slow
   * breath, from the Orb's own props. It also seeds `indicator`, the faint
   * resting mark, unless that is passed explicitly. See kit/media.ts. Omit
   * when nothing is playing.
   */
  media?: OrbMedia | null;
  /**
   * The faint mark inside the resting Orb: something is in the menu, and here
   * is a hint what. Derived from `media` when omitted, so audio needs no
   * wiring. Pass it explicitly for anything else — `mail` for an unread
   * message, and so on. See kit/media.ts for the rule this carves out of.
   */
  indicator?: OrbIndicator | null;
  /** Controlled open state. Omit to let the component own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
}

type ArcStyle = CSSProperties & { '--arc-x'?: string; '--fan-i'?: number };

/**
 * Position on the curve, plus the item's distance from the Orb in steps.
 *
 * `--fan-i` is what makes the fan arrive as a sequence rather than a block:
 * the Orb ignites, the energy path travels, then HOTTIE lands, then WORLDS,
 * and so on outward. Counting is from the Orb — item 0 is the one nearest the
 * thumb — for the same reason fanGeometry counts that way.
 */
const arc = (px: number, fromOrb: number): ArcStyle => ({
  '--arc-x': `${px.toFixed(2)}px`,
  '--fan-i': fromOrb,
});

/**
 * How long the Orb has the screen to itself before the fan follows.
 *
 * The Orb grows, brightens and tightens its bloom first; only then does the
 * path travel and the labels land. Without this beat the Orb and the menu read
 * as two unrelated things that happened at the same moment.
 */
const IGNITION_MS = 170;

export const OrbFan = ({
  product,
  activeRoute,
  context = [],
  hand = HAND_DEFAULT,
  hottie = true,
  omitRoutes,
  fanRoutes,
  orbState = 'resting',
  orbSize = 56,
  routeIcons,
  onNavigate,
  onContext,
  onHottie,
  hottieMessages,
  onHottieSend,
  hottieVoice,
  media,
  indicator,
  open: openProp,
  onOpenChange,
  className,
}: OrbFanProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [igniting, setIgniting] = useState(false);
  const [hottieOpen, setHottieOpen] = useState(false);
  const [hottieMode, setHottieMode] = useState<HottieMode>('chat');
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const fanId = useId().replace(/:/g, '');
  const rootRef = useRef<HTMLDivElement>(null);
  const voice = useHottieVoice(hottieOpen && !onHottie);

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) closeWorldsMenu();
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const openHottieSheet = useCallback(() => {
    setOpen(false);
    if (onHottie) {
      onHottie();
      return;
    }
    if (!hottie) return;
    setHottieOpen(true);
    // setHottieOpen is a stable useState setter, so listing it changes nothing
    // at runtime — but React Compiler compares its inferred dependencies against
    // this array and skips optimising the whole component when they disagree.
  }, [hottie, onHottie, setOpen, setHottieOpen]);

  const { onPointerDown, finishPress, clearHold } = useOrbHold(openHottieSheet);

  const routes = fanRoutes
    ? [...fanRoutes]
    : worldRoutes(product, { hottie, omit: omitRoutes });
  const icons = routeIcons ? { ...ROUTE_ICONS, ...routeIcons } : ROUTE_ICONS;

  // Media rows ride at the END of the routes run rather than in a group of
  // their own. DOM order is top-to-bottom with the Orb at the bottom, so last
  // is nearest the thumb — and fanGeometry keeps every number it already had.
  // A third group would have needed its own pitch, its own gap and its own
  // term in fanPath, for a row that is usually not there.
  const mediaRows = media ? (media.onOpen ? 2 : 1) : 0;
  const slots = routes.length + mediaRows;
  const offsets = fanOffsets(slots, context.length);
  const path = fanPath(slots, context.length);

  // The Orb's own beat. Runs on open only — closing is immediate, because a
  // menu you are dismissing should never make you wait for choreography.
  useEffect(() => {
    if (!open) {
      setIgniting(false);
      return;
    }
    setIgniting(true);
    const t = window.setTimeout(() => setIgniting(false), IGNITION_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  const onPointerUp = () => {
    if (finishPress()) return;
    // Tap opens the existing submenu in place. Not a redirect, not world home,
    // not Hottie. If Hottie is open, tap dismisses it and toggles the fan.
    if (hottieOpen) {
      setHottieOpen(false);
      setHottieMode('chat');
    }
    setOpen(!open);
  };

  // Escape closes, and a tap anywhere off the fan closes. Both are the same
  // promise: the fan is a transient state, never somewhere you get stuck.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open, setOpen]);

  const go = (route: string) => {
    setOpen(false);
    // HOTTIE is never a normal route — it is the assistant, reached the same
    // way the hold reaches it. Do not call onNavigate for it.
    if (route === 'hottie') {
      openHottieSheet();
      return;
    }
    // The worlds door opens the header world picker (same menu as the wordmark).
    if (route === 'passport' && openWorldsMenu()) return;
    onNavigate?.(route);
  };

  const item = (
    label: string,
    style: ArcStyle,
    opts: { route?: string; current?: boolean; onSelect: () => void },
  ) => {
    const glyph = opts.route ? icons[opts.route] : undefined;
    return (
      <button
        key={label}
        type="button"
        className="hottr-fan__item"
        data-route={opts.route}
        aria-current={opts.current ? 'true' : undefined}
        style={style}
        tabIndex={open ? 0 : -1}
        onClick={opts.onSelect}
      >
        {glyph && <Icon name={glyph} size={20} className="ms" />}
        <span>{label}</span>
      </button>
    );
  };

  // Brightness follows the beat: hardest at the moment of ignition, then it
  // settles back while the fan is held open. A constant "open" brightness reads
  // as a light switch; this reads as the Orb doing something.
  const openIntensity = igniting ? 1.28 : 1.12;

  // Playing reads as `interested` at a raised intensity — the Orb's own
  // material, warmer and tighter, with a slow breath added by fan-media.css.
  // It is NOT `activated`: that state means the fan is open and nothing else.
  // The resting mark is a separate thing — `indicator`, below — and not part
  // of this state.
  const playing = mediaIsLive(media);
  const restState: OrbState = playing ? 'interested' : orbState;
  const live =
    voice ?? {
      state: (open ? 'activated' : restState) as OrbState,
      intensity: open ? openIntensity : playing ? 1.14 : 1,
    };

  // RESTING is the state that has to carry this. The whole point is that you
  // can see there is something in the menu WITHOUT opening it — so it shows
  // whenever the fan is shut, playing or paused or a message waiting, and it
  // goes the moment the fan is open, because by then you are looking straight
  // at the thing it was pointing at.
  const mark = indicator === undefined ? mediaIndicator(media) : indicator;

  return (
    <div ref={rootRef} className={cn('hottr-dock', className)} data-hand={hand}>
      {/* Dims the content while the fan is open so 11px labels stay readable
          over photography. Tapping it closes, which is the same gesture as
          tapping anywhere outside the fan. */}
      <div
        className="hottr-fan-scrim"
        data-visible={open ? 'true' : 'false'}
        aria-hidden
        onPointerDown={() => setOpen(false)}
      />
      <nav
        id={fanId}
        className="hottr-fan"
        data-open={open ? 'true' : 'false'}
        aria-label="Routes"
        aria-hidden={open ? undefined : true}
      >
        {/* The energy path. Drawn from the SAME sine the labels sit on, so the
            line and the labels cannot disagree. `pathLength={1}` normalises the
            stroke so the draw-on works without measuring the DOM. */}
        <svg
          className="hottr-fan__path"
          viewBox={`0 0 ${path.width} ${path.height}`}
          width={path.width}
          height={path.height}
          fill="none"
          aria-hidden
          focusable="false"
        >
          {/* The <g> exists so --arc-scale can scale the curve about the
              anchored edge (SVG local x=0), independently of the mirror the
              right-handed fan applies to the whole svg. Without the wrapper the
              two transforms would share an origin and fight. */}
          <g>
            <path d={path.d} pathLength={1} vectorEffect="non-scaling-stroke" />
          </g>
        </svg>

        {context.length > 0 && (
          <div className="hottr-fan__group hottr-fan__group--context">
            {context.map((action, i) =>
              item(action, arc(offsets.context[i], routes.length + (context.length - 1 - i)), {
                onSelect: () => {
                  setOpen(false);
                  onContext?.(action);
                },
              }),
            )}
          </div>
        )}

        {context.length > 0 && (
          <div
            className="hottr-fan__hairline"
            style={arc(offsets.hairline, routes.length)}
            aria-hidden
          />
        )}

        <div className="hottr-fan__group hottr-fan__group--routes">
          {routes.map((route, i) =>
            item(routeLabel(route).toUpperCase(), arc(offsets.routes[i], slots - 1 - i), {
              route,
              current: route === activeRoute,
              onSelect: () => go(route),
            }),
          )}

          {media?.onOpen && (
            <button
              type="button"
              className="hottr-fan__item hottr-fan__item--media"
              data-media="title"
              style={arc(offsets.routes[routes.length], 1)}
              tabIndex={open ? 0 : -1}
              onClick={() => {
                setOpen(false);
                media.onOpen?.();
              }}
            >
              <span className="hottr-fan__media-title">{media.title}</span>
            </button>
          )}

          {media && (
            <button
              type="button"
              className="hottr-fan__item hottr-fan__item--media"
              data-media={media.status}
              // Not setOpen(false): pausing is a thing you do to what is
              // playing, not a thing you do instead of using the menu. Closing
              // here would make resuming a second full gesture.
              style={arc(offsets.routes[slots - 1], 0)}
              tabIndex={open ? 0 : -1}
              onClick={media.onToggle}
            >
              <Icon name={mediaToggleIcon(media)} size={20} className="ms" />
              <span>{mediaToggleLabel(media).toUpperCase()}</span>
            </button>
          )}
        </div>
      </nav>

      <button
        type="button"
        className="hottr-orb-slot"
        data-open={open ? 'true' : 'false'}
        data-igniting={igniting ? 'true' : 'false'}
        data-media={playing ? 'playing' : undefined}
        aria-expanded={open}
        aria-controls={fanId}
        aria-label={open ? 'Close routes. Hold for Hottie' : 'Open routes. Hold for Hottie'}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={clearHold}
        onPointerLeave={clearHold}
        onContextMenu={(e) => e.preventDefault()}
      >
        <HottrOrb
          product={product}
          size={orbSize}
          state={live.state}
          intensity={live.intensity}
          level="living"
        />
        {mark && !open && (
          <span className="hottr-orb-slot__mark" role="img" aria-label={mark.label}>
            <Icon name={mark.icon} size={Math.round(orbSize * 0.36)} className="ms" aria-hidden />
          </span>
        )}
      </button>

      {!onHottie && (
        <HottieSheet
          open={hottieOpen}
          onOpenChange={(next) => {
            setHottieOpen(next);
            if (!next) setHottieMode('chat');
          }}
          mode={hottieMode}
          onModeChange={setHottieMode}
          messages={hottieMessages}
          onSend={onHottieSend}
          voice={hottieVoice}
        />
      )}
    </div>
  );
};
